import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FlaskConical, MoreHorizontal, Pencil, Smartphone, X } from 'lucide-react';
import { useState } from 'react';
import { DeviceCell, DeviceSheet, DeviceStatusBadge, humanize, PermissionBadge } from '@/components/installations/device-sheet';
import { TestDeviceDialog } from '@/components/installations/test-device-dialog';
import { CopyButton } from '@/components/shared/copy-button';
import { EmptyState } from '@/components/shared/empty-state';
import { FormField } from '@/components/shared/form-field';
import { TableSkeleton } from '@/components/shared/loading';
import { Pagination, useCursorPages } from '@/components/shared/pagination';
import { SectionCard } from '@/components/shared/section-card';
import { SimpleSelect } from '@/components/shared/simple-select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import { useToast } from '@/lib/toast';
import type { Installation, Page } from '@/lib/types';
import { useAppCtx } from './app-layout';

export function Installations() {
  const { app } = useAppCtx();
  const qc = useQueryClient();
  const [platform, setPlatform] = useState('');
  const [active, setActive] = useState('true');
  const [testDevice, setTestDevice] = useState('');
  const [permission, setPermission] = useState('');
  const [user, setUser] = useState('');
  const [tag, setTag] = useState('');
  const [naming, setNaming] = useState<Installation | null>(null);
  const [viewing, setViewing] = useState<Installation | null>(null);
  const toast = useToast();

  const filters = {
    platform, active, test_device: testDevice, notification_permission: permission || undefined,
    external_user_id: user, tag: /^[^:]+:.+$/.test(tag) ? tag : undefined,
  };
  const pages = useCursorPages({
    queryKey: ['installations', app.id, filters],
    fetchPage: (before, limit) => api<Page<Installation>>(`/admin/v1/apps/${app.id}/installations`, { query: { ...filters, before, limit } }),
  });
  const list = pages.query;
  // Adding and renaming go through TestDeviceDialog (a name is required); removing is one click.
  const removeTest = useMutation({
    mutationFn: (d: Installation) =>
      api(`/admin/v1/apps/${app.id}/installations/${d.installation_id}`, { method: 'PATCH', body: { is_test_device: false } }),
    onSuccess: (_r, d) => {
      toast(`${d.test_device_name ?? 'Device'} removed from test devices`);
      void qc.invalidateQueries({ queryKey: ['installations', app.id] });
    },
  });
  const rows = pages.rows;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <FormField label="Platform" className="w-36">
          <SimpleSelect value={platform} onValueChange={setPlatform} options={[{ value: '', label: 'All' }, { value: 'android', label: 'Android' }, { value: 'ios', label: 'iOS' }]} />
        </FormField>
        <FormField label="Status" className="w-36">
          <SimpleSelect value={active} onValueChange={setActive} options={[{ value: '', label: 'All' }, { value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]} />
        </FormField>
        <FormField label="Permission" className="w-40">
          <SimpleSelect
            value={permission} onValueChange={setPermission}
            options={[{ value: '', label: 'All' }, ...(['authorized', 'provisional', 'denied', 'not_determined'] as const).map((p) => ({ value: p, label: humanize(p) }))]}
          />
        </FormField>
        <FormField label="Device type" className="w-40">
          <SimpleSelect value={testDevice} onValueChange={setTestDevice} options={[{ value: '', label: 'All' }, { value: 'true', label: 'Test devices' }, { value: 'false', label: 'Regular' }]} />
        </FormField>
        <FormField label="External User ID" htmlFor="user" className="w-56"><Input id="user" value={user} onChange={(e) => setUser(e.target.value)} placeholder="contains…" /></FormField>
        <FormField label="Tag (key:value)" htmlFor="tag" className="w-44"><Input id="tag" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="plan:pro" /></FormField>
      </div>
      <SectionCard flush className={list.isPlaceholderData ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {list.isLoading ? <TableSkeleton /> : rows.length === 0 ? (
          testDevice === 'true'
            ? <EmptyState icon={FlaskConical} title="No test devices" hint="Set Device type to All, then open a device's ⋯ menu and choose Add as test device." />
            : <EmptyState icon={Smartphone} title="No devices" hint="Devices appear here once your app registers them with an SDK key." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Installation ID</TableHead><TableHead>Device</TableHead><TableHead className="whitespace-nowrap">External User ID</TableHead>
                <TableHead>App / OS</TableHead><TableHead>Status</TableHead><TableHead>Permission</TableHead><TableHead>Last seen</TableHead><TableHead className="pr-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((i) => (
                <TableRow
                  key={i.id} tabIndex={0} className="cursor-pointer" onClick={() => setViewing(i)}
                  onKeyDown={(e) => { if (e.key === 'Enter') setViewing(i); }}
                >
                  <TableCell className="pl-5 font-mono text-xs whitespace-nowrap"><span className="inline-flex items-center gap-2">{i.installation_id} <span onClick={(e) => e.stopPropagation()}><CopyButton value={i.installation_id} label={false} /></span></span></TableCell>
                  <TableCell className="whitespace-nowrap"><DeviceCell device={i} /></TableCell>
                  <TableCell className="font-mono text-xs">{i.external_user_id ?? '—'}</TableCell>
                  <TableCell>{[i.app_version, i.os_version].filter(Boolean).join(' / ') || '—'}</TableCell>
                  <TableCell><DeviceStatusBadge device={i} /></TableCell>
                  <TableCell><PermissionBadge value={i.notification_permission} /></TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(i.last_seen_at)}</TableCell>
                  {/* The menu and its items must not also open the detail sheet. */}
                  <TableCell className="pr-5 text-right" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${i.installation_id}`}><MoreHorizontal /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="min-w-48">
                        {/* The dialog opens after the menu closes, or its focus return steals focus from it. */}
                        {i.is_test_device ? (
                          <>
                            <DropdownMenuItem onSelect={() => setTimeout(() => setNaming(i), 0)}><Pencil /> Rename test device</DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => removeTest.mutate(i)}><X /> Remove from test devices</DropdownMenuItem>
                          </>
                        ) : (
                          <DropdownMenuItem onSelect={() => setTimeout(() => setNaming(i), 0)}><FlaskConical /> Add as test device</DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pagination p={pages} noun="devices" />
      </SectionCard>

      <DeviceSheet
        appId={app.id} device={viewing} onClose={() => setViewing(null)}
        onNameTest={setNaming} onRemoveTest={(d) => removeTest.mutate(d)} removing={removeTest.isPending}
      />
      <TestDeviceDialog appId={app.id} device={naming} onClose={() => setNaming(null)} />
    </div>
  );
}
